import { useState, useEffect } from 'react';
import { Modal, Backdrop, Box, Typography } from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import CancelIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';

import { useTextStyles } from 'src/hooks/useTextStyles';
import Button from 'src/components/NewButton';
import { CampaignProps } from 'src/types/campaign';

const useStyles = makeStyles(() =>
  createStyles({
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 16,
    },
    searchContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: '8px 12px',
      border: '1px solid #D0D5DD',
      borderRadius: 8,
      marginBottom: 12,
    },
    searchInput: {
      border: 'none',
      outline: 'none',
      flex: 1,
      fontSize: 14,
    },
    content: {
      width: '100%',
      maxHeight: 400,
      overflowY: 'auto',
    },
    option: {
      display: 'flex',
      alignItems: 'center',
      padding: '12px 16px',
      border: '1px solid #EAECF0',
      borderRadius: 8,
      marginBottom: 8,
      cursor: 'pointer',
      '&:hover': {
        background: '#F9FAFB',
      },
    },
    emptyState: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 40,
      color: '#667085',
    },
    buttons: {
      paddingTop: 16,
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      justifyContent: 'flex-end',
    },
  })
);

const modalStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '100%',
  maxWidth: 520,
  bgcolor: 'background.paper',
  background: '#fff',
  borderRadius: 8,
  boxShadow:
    '0px 8px 8px -4px rgba(16, 24, 40, 0.03), 0px 20px 24px -4px rgba(16, 24, 40, 0.08)',
  padding: 24,
};

interface CampaignSelectorModalProps {
  open: boolean;
  closeModal: () => void;
  campaigns: CampaignProps[];
  selectedCampaignIds: string[];
  onSelect: (campaigns: CampaignProps[]) => void;
}

export const CampaignSelectorModal = ({
  open,
  closeModal,
  campaigns,
  selectedCampaignIds,
  onSelect,
}: CampaignSelectorModalProps) => {
  const classes = useStyles();
  const textClasses = useTextStyles();
  const [searchKey, setSearchKey] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>(selectedCampaignIds);

  useEffect(() => {
    if (open) {
      setSearchKey('');
      setSelectedIds(selectedCampaignIds);
    }
  }, [open, selectedCampaignIds]);

  const filteredCampaigns = campaigns.filter(
    (campaign) =>
      !searchKey ||
      campaign.name.toLowerCase().includes(searchKey.toLowerCase())
  );

  const handleToggle = (campaignId: string) => {
    setSelectedIds((prev) =>
      prev.includes(campaignId)
        ? prev.filter((id) => id !== campaignId)
        : [...prev, campaignId]
    );
  };

  const handleConfirm = () => {
    onSelect(campaigns.filter((campaign) => selectedIds.includes(campaign.id)));
  };

  return (
    <Modal
      open={open}
      aria-labelledby='campaign-selector-modal-title'
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 800,
      }}
    >
      <Box sx={{ ...modalStyle }}>
        <Box className={classes.header}>
          <Typography
            className={textClasses.lgBold}
            style={{ color: '#101828' }}
          >
            Select Campaigns
          </Typography>
          <CancelIcon
            cursor='pointer'
            width={24}
            height={24}
            onClick={closeModal}
          />
        </Box>
        <Box className={classes.searchContainer}>
          <SearchIcon style={{ color: '#667085' }} />
          <input
            type='text'
            placeholder='Search campaigns...'
            value={searchKey}
            onChange={(e) => setSearchKey(e.target.value)}
            className={classes.searchInput}
          />
        </Box>
        <Box className={classes.content}>
          {campaigns.length === 0 ? (
            <Box className={classes.emptyState}>
              <Typography className={textClasses.smRegular}>
                No campaigns available.
              </Typography>
            </Box>
          ) : filteredCampaigns.length === 0 ? (
            <Box className={classes.emptyState}>
              <Typography className={textClasses.smRegular}>
                No campaigns match your search.
              </Typography>
            </Box>
          ) : (
            filteredCampaigns.map((campaign) => {
              const isSelected = selectedIds.includes(campaign.id);
              return (
                <Box
                  key={campaign.id}
                  className={classes.option}
                  onClick={() => handleToggle(campaign.id)}
                  style={{
                    background: isSelected ? '#ECFDF3' : undefined,
                    borderColor: isSelected ? '#5EA1F2' : undefined,
                  }}
                >
                  <Typography
                    className={textClasses.smBold}
                    style={{ color: '#344054' }}
                  >
                    {campaign.name}
                  </Typography>
                </Box>
              );
            })
          )}
        </Box>
        <Box className={classes.buttons}>
          <Button
            title='Cancel'
            variant='outlined'
            height={46}
            onClick={closeModal}
          />
          <Button
            title={`Confirm${
              selectedIds.length ? ` (${selectedIds.length})` : ''
            }`}
            height={46}
            onClick={handleConfirm}
            disabled={selectedIds.length === 0}
          />
        </Box>
      </Box>
    </Modal>
  );
};
